/**
 * Решения жизни, v1: 14 видов как данные (DESIGN §4.4, план Task 12).
 *
 * Каждое решение описывают четыре функции: `due` — наступил ли момент,
 * `options` — варианты с пометкой доступности (игроку предлагаются только
 * реально доступные: нет школы рядом — нет выбора «учиться»), `timeout` —
 * наиболее вероятный для такого человека вариант (автовыбор по DESIGN §4.6),
 * `apply` — эффект выбранного варианта. Порядок в таблице = приоритет:
 * в году не больше одного решения, берётся первое наступившее.
 *
 * Ключи текстов: вопрос `decision.<kind>`, вариант `option.<kind>.<id>`,
 * причина недоступности `reason.*`, запись журнала `decision.<kind>.<id>`
 * (с суффиксом исхода, если исход случайный). Сами тексты — в @life/i18n.
 *
 * Все случайности — из переданного `Rng`; состояния клампятся в [0, 100],
 * деньги и долг — конечные и ≥ 0; вход не мутируется. Пороговые возрасты и
 * коэффициенты — игровая калибровка **[моё]**, статистика входит через
 * показатели страны (schoolYears, tertiary, gdpPpp, oopHealth, urban,
 * fertility, births).
 */
import { getCountry, getValue, listCountries, type Dataset, type Indicator } from '@life/data';
import { t } from '@life/i18n';
import { expectedIncome, jobLevelFor, jobTitleKey, MAX_JOB_LEVEL, type JobLevel } from '../economy';
import { FLAG_PARENTS_DEAD, FLAG_ACCIDENT_YEAR, FLAG_LICENSE } from '../events/defs';
import { clampStats } from '../focus';
import { CONDITION_CHRONIC } from '../health';
import type { Rng } from '../rng';
import type { Education, Life, LogEntry, Stats } from '../types';

/* ---------- публичные типы ---------- */

export const DECISION_KINDS = [
  'school.primary',
  'school.lowerSec',
  'school.upperSec',
  'school.tertiary',
  'work.first',
  'work.change',
  'move.city',
  'move.abroad',
  'love.partner',
  'family.child',
  'health.treatment',
  'money.invest',
  'work.retire',
  'family.careParents',
] as const;

export type DecisionKind = (typeof DECISION_KINDS)[number];

export interface DecisionOption {
  id: string;
  labelKey: string;
  available: boolean;
  /** Почему вариант недоступен (ключ `reason.*`). */
  reasonKey?: string;
}

export interface Decision {
  /** `<kind>@<age>` — уникален в пределах жизни, так как в год не больше одного решения. */
  id: string;
  kind: DecisionKind;
  promptKey: string;
  options: DecisionOption[];
  /** Вариант автовыбора; всегда доступен. */
  timeoutOption: string;
}

export interface DecisionCtx {
  ds: Dataset;
  life: Life;
  /** Календарный год = birthYear + age. */
  year: number;
}

export interface DecisionDef {
  kind: DecisionKind;
  /**
   * Варианты «ничего не изменилось»: при автовыборе (таймаут, автопилот) они
   * не пишутся в журнал — иначе «с детьми решили подождать» повторялось по
   * 6 раз на жизнь. При явном выборе игрока запись остаётся.
   */
  silent?: readonly string[];
  due: (c: DecisionCtx) => boolean;
  options: (c: DecisionCtx, rng: Rng) => DecisionOption[];
  /** Автовыбор; `rng` — для редких вероятностных таймаутов (эмиграция), тот же, что у `options`. */
  timeout: (c: DecisionCtx, options: readonly DecisionOption[], rng: Rng) => string;
  apply: (c: DecisionCtx, optionId: string, rng: Rng) => { life: Life; entries: LogEntry[] };
}

/* ---------- флаги жизни, которые ведут решения ---------- */

/** Возраст последнего принятого решения: не больше одного в год. */
export const FLAG_DECISION_AGE = 'decisionAge';
/** Возраст найма на текущую работу (work.change каждые 7 лет). */
export const FLAG_JOB_START_AGE = 'jobStartAge';
/** Вышел на пенсию. */
export const FLAG_RETIRED = 'retired';
/** Годовая пенсия в долларах ППС. */
export const FLAG_PENSION = 'pension';
/** Решение о переезде в город уже принималось. */
export const FLAG_MOVE_CITY_DECIDED = 'moveCityDecided';
/** Решение об эмиграции уже принималось. */
export const FLAG_ABROAD_DECIDED = 'abroadDecided';
/** Есть спонсор переезда (член когорты); `sponsorIso3` — его страна. */
export const FLAG_SPONSOR = 'sponsor';
export const FLAG_SPONSOR_ISO3 = 'sponsorIso3';
/** Возраст последнего вопроса о партнёре. */
export const FLAG_LOVE_ASKED_AGE = 'loveAskedAge';
/** Возраст последнего вопроса о ребёнке. */
export const FLAG_CHILD_ASKED_AGE = 'childAskedAge';
/** Хронический диагноз уже обсуждался с врачом. */
export const FLAG_CHRONIC_DECIDED = 'chronicDecided';
/** Год аварии, по которой лечение уже решено. */
export const FLAG_ACCIDENT_DECIDED_YEAR = 'accidentDecidedYear';
/** Возраст последнего вопроса об инвестициях. */
export const FLAG_INVEST_ASKED_AGE = 'investAskedAge';
/** Возраст последнего вопроса о пенсии. */
export const FLAG_RETIRE_ASKED_AGE = 'retireAskedAge';
/** Уход за родителями уже решён. */
export const FLAG_CARE_DECIDED = 'careParentsDecided';
/** Хоть раз работал: повторный найм после увольнения пишется как «новая работа». */
export const FLAG_EVER_WORKED = 'everWorked';

/* ---------- константы правил ---------- */

const PRIMARY_AGE = 6;
const LOWER_SEC_AGE = 12;
const UPPER_SEC_AGE = 15;
const TERTIARY_AGE = 18;
/** Возраст, в котором учёба данной ступени заканчивается и начинается работа. */
const SCHOOL_END_AGE: Readonly<Record<Education, number>> = {
  none: PRIMARY_AGE,
  primary: LOWER_SEC_AGE,
  lowerSec: UPPER_SEC_AGE,
  upperSec: TERTIARY_AGE,
  vocational: 18,
  university: 22,
};
/** Школа может быть недоступна: средние годы обучения в стране ниже и семья бедная. */
const NO_SCHOOL_MAX_YEARS = 4;
const NO_SCHOOL_MAX_DECILE = 3;
const NO_SCHOOL_CHANCE = 0.5;
/** Автовыбор школы по средним годам обучения в стране. */
const LOWER_SEC_TIMEOUT_YEARS = 6;
const VOCATIONAL_TIMEOUT_YEARS = 8;
const UPPER_SEC_TIMEOUT_TERTIARY = 30;
/** Вуз: знания и достаток либо охват вузами в стране. */
const TERTIARY_MIN_KNOWLEDGE = 55;
const TERTIARY_MIN_DECILE = 5;
const TERTIARY_MIN_ENROLMENT = 40;
/** Работа возможна с 12 лет (детский труд — статистическая реальность бедных стран) и до пенсии. */
const WORK_MIN_AGE = 12;
const RETIRE_MIN_AGE = 60;
const RETIRE_MAX_AGE = 67;
const RETIRE_TIMEOUT_AGE = 65;
const RETIRE_ASK_EVERY = 3;
const JOB_CHANGE_EVERY = 7;
const JOB_CHANGE_HAPPINESS = 3;
/** Пенсия: доля последнего дохода по достатку страны. */
const PENSION_RICH_GDP = 15_000;
const PENSION_RICH_SHARE = 0.4;
const PENSION_POOR_SHARE = 0.1;
const MOVE_CITY_MIN_AGE = 18;
const MOVE_CITY_MAX_AGE = 30;
const MOVE_CITY_COST_GDP = 0.2;
const MOVE_CITY_TIMEOUT_URBAN = 50;
export const ABROAD_MIN_AGE = 20;
export const ABROAD_MAX_AGE = 40;
export const ABROAD_MONEY_GDP = 2;
export const ABROAD_COST_GDP = 1;
export const ABROAD_BASE_CHANCE = 0.4;
/**
 * Доля людей с возможностью уехать, для которых автовыбор — «уехать».
 * Эмиграция — редкий путь: с 0.1 и успехом 0.4 при ~75 % жизней, доходящих
 * до решения, уезжает ≈ 3 % (цель этапа 1: 1–5 %).
 */
export const ABROAD_TIMEOUT_GO = 0.1;
export const ABROAD_SPONSOR_BONUS = 0.3;
const ABROAD_FAIL_HAPPINESS = -3;
const LOVE_MIN_AGE = 20;
const LOVE_MAX_AGE = 35;
const LOVE_ASK_EVERY = 3;
const CHILD_MIN_AGE = 20;
const CHILD_MAX_AGE = 42;
const CHILD_ASK_EVERY = 2;
const CHILD_COST_GDP = 0.1;
export const TREAT_COST_GDP = 0.3;
export const TREAT_HEALTH = 15;
export const WAIT_HEALTH = -5;
/**
 * Крючки мини-игр этапов жизни (план 2b, Task 7): исход игры ставит флаг
 * в том же году, что и ответ на решение, решение читает его вместо своей
 * константы и снимает. Так движок остаётся единственным местом, где
 * решение меняет жизнь.
 */
/** «Очередь» (G8): здоровье за лечение вместо TREAT_HEALTH. */
export const FLAG_TREATMENT_HEALTH = 'treatmentHealth';
/** «Очередь» (G8): доля полной цены лечения, 0..1. */
export const FLAG_TREATMENT_COST_SHARE = 'treatmentCostShare';
/** «Чемодан»/«Граница» (G9/G10): поправка к шансу эмиграции, складывается. */
export const FLAG_ABROAD_CHANCE_DELTA = 'abroadChanceDelta';
/** «Чемодан» (G9): +уровень при следующем найме, снимается при найме. */
export const FLAG_JOB_LEVEL_BONUS = 'jobLevelBonus';
const INVEST_MONEY_TO_INCOME = 3;
const INVEST_ASK_EVERY = 5;
const INVEST_WIN_CHANCE = 0.6;
const INVEST_WIN_MULT = 1.3;
const INVEST_LOSS_MULT = 0.7;
const CARE_MIN_AGE = 45;
const CARE_MAX_AGE = 60;
const CARE_COST_GDP = 0.1;
const PARENTS = 2;

/* ---------- помощники ---------- */

function clampMoney(x: number): number {
  return Number.isFinite(x) && x > 0 ? x : 0;
}

function ind(c: DecisionCtx, name: Indicator): number {
  return clampMoney(getValue(c.ds, c.life.iso3, name, c.year).v);
}

const inAge = (life: Life, lo: number, hi: number): boolean => life.age >= lo && life.age <= hi;
const num = (life: Life, key: string): number | undefined => {
  const v = life.flags[key];
  return typeof v === 'number' ? v : undefined;
};
const askedRecently = (life: Life, key: string, every: number): boolean => {
  const asked = num(life, key);
  return asked !== undefined && life.age - asked < every;
};
const parentsAlive = (life: Life): number => PARENTS - Math.min(PARENTS, num(life, FLAG_PARENTS_DEAD) ?? 0);
const retired = (life: Life): boolean => Boolean(life.flags[FLAG_RETIRED]);
const hasChronic = (life: Life): boolean => life.conditions.includes(CONDITION_CHRONIC);

/** Сдвиг состояний с клампом; остальные поля жизни — через overrides. */
function shift(life: Life, delta: Partial<Stats>, overrides: Partial<Life> = {}): Life {
  const stats = clampStats({
    health: life.stats.health + (delta.health ?? 0),
    happiness: life.stats.happiness + (delta.happiness ?? 0),
    knowledge: life.stats.knowledge + (delta.knowledge ?? 0),
    relations: life.stats.relations + (delta.relations ?? 0),
  });
  return { ...life, ...overrides, stats };
}

function withFlags(life: Life, patch: Record<string, number | boolean | string>): Life {
  return { ...life, flags: { ...life.flags, ...patch } };
}

/** Трата из кошелька; нехватка либо в долг (медицина), либо просто не тратится. */
function spend(life: Life, cost: number, intoDebt: boolean): Life {
  const money = clampMoney(life.money);
  const c = clampMoney(cost);
  if (c <= money) return { ...life, money: money - c };
  return { ...life, money: 0, debt: intoDebt ? clampMoney(life.debt) + (c - money) : clampMoney(life.debt) };
}

const opt = (kind: DecisionKind, id: string, available = true, reasonKey?: string): DecisionOption =>
  reasonKey === undefined
    ? { id, labelKey: `option.${kind}.${id}`, available }
    : { id, labelKey: `option.${kind}.${id}`, available, reasonKey };

const entry = (life: Life, kind: DecisionKind, suffix: string, params?: LogEntry['params']): LogEntry =>
  params ? { age: life.age, key: `decision.${kind}.${suffix}`, params } : { age: life.age, key: `decision.${kind}.${suffix}` };

const yesNo = (kind: DecisionKind, yes: string, no: string): DecisionOption[] => [opt(kind, yes), opt(kind, no)];

/** Первый доступный вариант среди предпочтений (последний — запасной, обязан быть доступен). */
function firstAvailable(options: readonly DecisionOption[], ...preferred: string[]): string {
  for (const id of preferred) {
    if (options.find((o) => o.id === id)?.available) return id;
  }
  const any = options.find((o) => o.available);
  if (!any) throw new RangeError('decision without available options');
  return any.id;
}

/** Права (мини-игра F5, `flags.license`) открывают работу на уровень выше при найме и смене работы. */
export const LICENSE_JOB_LEVEL_BONUS = 1;
/**
 * Уровень места по итогам собеседования (мини-игра F2): исход игры ставит
 * флаг в том же году, что и ответ на `work.change`, и `hireLevel` берёт его
 * вместо жребия. Снимается при найме и смене работы.
 */
export const FLAG_INTERVIEW_LEVEL = 'interviewLevel';

function interviewLevel(life: Life): JobLevel | null {
  const v = life.flags[FLAG_INTERVIEW_LEVEL];
  if (typeof v !== 'number' || !Number.isInteger(v)) return null;
  return Math.max(0, Math.min(MAX_JOB_LEVEL, v)) as JobLevel;
}

/** Уровень при найме: итог собеседования, иначе `jobLevelFor` плюс бонус за права, не выше MAX_JOB_LEVEL. */
function hireLevel(c: DecisionCtx, rng: Rng): JobLevel {
  const interviewed = interviewLevel(c.life);
  if (interviewed !== null) return interviewed;
  const drawn = jobLevelFor(c.life.education, c.life.stats.knowledge, rng);
  const bonus = (c.life.flags[FLAG_LICENSE] === true ? LICENSE_JOB_LEVEL_BONUS : 0) + Math.max(0, Math.trunc(num(c.life, FLAG_JOB_LEVEL_BONUS) ?? 0));
  return Math.min(MAX_JOB_LEVEL, drawn + bonus) as JobLevel;
}

function withoutFlag(life: Life, ...keys: string[]): Life {
  if (!keys.some((key) => key in life.flags)) return life;
  const flags: Life['flags'] = {};
  for (const [k, v] of Object.entries(life.flags)) if (!keys.includes(k)) flags[k] = v;
  return { ...life, flags };
}

/** Флаги собеседования и «чемодана» снимаются при найме. */
const HIRE_FLAGS = [FLAG_INTERVIEW_LEVEL, FLAG_JOB_LEVEL_BONUS];

/** Новая работа заданного уровня с доходом, разыгранным как для этого человека. */
function hire(c: DecisionCtx, level: JobLevel, rng: Rng): Life {
  const job = { titleKey: jobTitleKey(level), level, income: 0 };
  const income = expectedIncome(c.ds, { ...c.life, job }, rng);
  return withFlags(
    { ...c.life, job: { ...job, income: clampMoney(income) }, inSchool: false },
    { [FLAG_JOB_START_AGE]: c.life.age, [FLAG_EVER_WORKED]: true },
  );
}

/** Страны богаче текущей по ВВП на душу в этом году, с весами по рождениям. */
function richerCountries(c: DecisionCtx): { iso3s: string[]; weights: number[] } {
  const own = ind(c, 'gdpPpp');
  const iso3s: string[] = [];
  const weights: number[] = [];
  for (const rec of listCountries(c.ds)) {
    if (rec.iso3 === c.life.iso3) continue;
    if (getValue(c.ds, rec.iso3, 'gdpPpp', c.year).v > own) {
      const w = clampMoney(getValue(c.ds, rec.iso3, 'births', c.year).v);
      if (w > 0) {
        iso3s.push(rec.iso3);
        weights.push(w);
      }
    }
  }
  return { iso3s, weights };
}

function sponsorIso3(c: DecisionCtx): string | null {
  const v = c.life.flags[FLAG_SPONSOR_ISO3];
  if (typeof v !== 'string' || v === c.life.iso3) return null;
  return c.ds.countries.some((r) => r.iso3 === v) ? v : null;
}

const canAfford = (c: DecisionCtx): boolean => clampMoney(c.life.money) >= ABROAD_MONEY_GDP * ind(c, 'gdpPpp');
const hasSponsor = (c: DecisionCtx): boolean => Boolean(c.life.flags[FLAG_SPONSOR]);

/** Лечение хронической болезни или последствий аварии ещё не решалось. */
function treatmentDue(c: DecisionCtx): boolean {
  const { life } = c;
  if (hasChronic(life) && !life.flags[FLAG_CHRONIC_DECIDED]) return true;
  const accident = num(life, FLAG_ACCIDENT_YEAR);
  return accident !== undefined && accident >= c.year - 1 && num(life, FLAG_ACCIDENT_DECIDED_YEAR) !== accident;
}

/* ---------- таблица ---------- */

export const DECISION_DEFS: readonly DecisionDef[] = [
  {
    kind: 'school.primary',
    due: ({ life }) => life.age === PRIMARY_AGE && life.education === 'none' && !life.inSchool,
    options: (c, rng) => {
      const noSchool =
        ind(c, 'schoolYears') < NO_SCHOOL_MAX_YEARS && c.life.familyDecile <= NO_SCHOOL_MAX_DECILE && rng.chance(NO_SCHOOL_CHANCE);
      return [opt('school.primary', 'study', !noSchool, noSchool ? 'reason.noSchoolNearby' : undefined), opt('school.primary', 'skip')];
    },
    timeout: (_c, options) => firstAvailable(options, 'study', 'skip'),
    apply: (c, id) => {
      const life =
        id === 'study'
          ? shift(c.life, { knowledge: 2 }, { inSchool: true, education: 'primary' })
          : shift(c.life, { happiness: 1 }, { inSchool: false });
      return { life, entries: [entry(life, 'school.primary', id)] };
    },
  },
  {
    kind: 'school.lowerSec',
    due: ({ life }) => life.age === LOWER_SEC_AGE && life.education === 'primary' && life.inSchool,
    options: () => yesNo('school.lowerSec', 'study', 'skip'),
    timeout: (c, options) => firstAvailable(options, ind(c, 'schoolYears') >= LOWER_SEC_TIMEOUT_YEARS ? 'study' : 'skip', 'study'),
    apply: (c, id) => {
      const life =
        id === 'study'
          ? shift(c.life, { knowledge: 2 }, { education: 'lowerSec' })
          : shift(c.life, { relations: 2 }, { inSchool: false });
      return { life, entries: [entry(life, 'school.lowerSec', id)] };
    },
  },
  {
    kind: 'school.upperSec',
    due: ({ life }) => life.age === UPPER_SEC_AGE && life.education === 'lowerSec' && life.inSchool,
    options: () => [opt('school.upperSec', 'school'), opt('school.upperSec', 'vocational'), opt('school.upperSec', 'work')],
    timeout: (c, options) => {
      const preferred =
        ind(c, 'tertiary') >= UPPER_SEC_TIMEOUT_TERTIARY ? 'school' : ind(c, 'schoolYears') >= VOCATIONAL_TIMEOUT_YEARS ? 'vocational' : 'work';
      return firstAvailable(options, preferred, 'school');
    },
    apply: (c, id) => {
      let life: Life;
      if (id === 'school') life = shift(c.life, { knowledge: 2 }, { education: 'upperSec' });
      else if (id === 'vocational') life = shift(c.life, { knowledge: 1 }, { education: 'vocational' });
      else life = shift(c.life, { relations: 2 }, { inSchool: false });
      return { life, entries: [entry(life, 'school.upperSec', id)] };
    },
  },
  {
    kind: 'school.tertiary',
    due: ({ life }) => life.age === TERTIARY_AGE && life.education === 'upperSec' && life.inSchool,
    options: (c) => {
      const smart = c.life.stats.knowledge >= TERTIARY_MIN_KNOWLEDGE;
      const affordable = c.life.familyDecile >= TERTIARY_MIN_DECILE || ind(c, 'tertiary') >= TERTIARY_MIN_ENROLMENT;
      const reason = !smart ? 'reason.lowKnowledge' : !affordable ? 'reason.noMoney' : undefined;
      return [opt('school.tertiary', 'university', smart && affordable, reason), opt('school.tertiary', 'work')];
    },
    timeout: (c, options) =>
      firstAvailable(options, ind(c, 'tertiary') >= TERTIARY_MIN_ENROLMENT ? 'university' : 'work', 'work'),
    apply: (c, id) => {
      const life =
        id === 'university'
          ? shift(c.life, { knowledge: 3, happiness: 2 }, { education: 'university' })
          : shift(c.life, { relations: 1 }, { inSchool: false });
      return { life, entries: [entry(life, 'school.tertiary', id)] };
    },
  },
  {
    kind: 'work.first',
    due: ({ life }) =>
      life.job === null &&
      !retired(life) &&
      life.age >= WORK_MIN_AGE &&
      life.age < RETIRE_MIN_AGE &&
      (!life.inSchool || life.age >= SCHOOL_END_AGE[life.education]),
    options: (c, rng) => {
      const top = hireLevel(c, rng);
      const options: DecisionOption[] = [];
      for (let level = 0; level <= MAX_JOB_LEVEL; level += 1) {
        options.push(opt('work.first', `level${level}`, level <= top, level <= top ? undefined : 'reason.noQualification'));
      }
      return options;
    },
    timeout: (_c, options) => {
      const available = options.filter((o) => o.available);
      return firstAvailable(options, available[available.length - 1]?.id ?? 'level0');
    },
    apply: (c, id, rng) => {
      const level = Number(id.slice('level'.length)) as JobLevel;
      const rehired = Boolean(c.life.flags[FLAG_EVER_WORKED]);
      const life = withoutFlag(shift(hire(c, level, rng), { happiness: 3 }), ...HIRE_FLAGS);
      return { life, entries: [entry(life, 'work.first', rehired ? 'rehired' : 'hired', { job: t(life.job!.titleKey) })] };
    },
  },
  {
    kind: 'work.change',
    silent: ['stay'],
    due: ({ life }) => {
      if (life.job === null || retired(life)) return false;
      const started = num(life, FLAG_JOB_START_AGE);
      if (started === undefined) return false;
      const worked = life.age - started;
      return worked > 0 && worked % JOB_CHANGE_EVERY === 0;
    },
    options: () => yesNo('work.change', 'change', 'stay'),
    timeout: (_c, options) => firstAvailable(options, 'stay'),
    apply: (c, id, rng) => {
      if (id !== 'change') {
        const life = withoutFlag(withFlags(c.life, { [FLAG_JOB_START_AGE]: c.life.age }), ...HIRE_FLAGS);
        return { life, entries: [entry(life, 'work.change', 'stay')] };
      }
      const current = c.life.job!.level;
      const drawn = hireLevel(c, rng);
      const level = Math.max(current, drawn) as JobLevel;
      const life = withoutFlag(shift(hire(c, level, rng), { happiness: JOB_CHANGE_HAPPINESS }), ...HIRE_FLAGS);
      return { life, entries: [entry(life, 'work.change', 'change', { job: t(life.job!.titleKey) })] };
    },
  },
  {
    kind: 'move.city',
    due: ({ life }) => !life.urban && inAge(life, MOVE_CITY_MIN_AGE, MOVE_CITY_MAX_AGE) && !life.flags[FLAG_MOVE_CITY_DECIDED],
    options: () => yesNo('move.city', 'move', 'stay'),
    timeout: (c, options) => firstAvailable(options, ind(c, 'urban') >= MOVE_CITY_TIMEOUT_URBAN ? 'move' : 'stay', 'stay'),
    apply: (c, id) => {
      const flagged = withFlags(c.life, { [FLAG_MOVE_CITY_DECIDED]: true });
      const life =
        id === 'move'
          ? shift(spend(flagged, MOVE_CITY_COST_GDP * ind(c, 'gdpPpp'), false), { relations: -4, knowledge: 2, happiness: 1 }, { urban: true })
          : shift(flagged, { relations: 2 });
      return { life, entries: [entry(life, 'move.city', id)] };
    },
  },
  {
    kind: 'move.abroad',
    due: (c) =>
      inAge(c.life, ABROAD_MIN_AGE, ABROAD_MAX_AGE) &&
      !c.life.flags[FLAG_ABROAD_DECIDED] &&
      (canAfford(c) || hasSponsor(c)) &&
      (sponsorIso3(c) !== null || richerCountries(c).iso3s.length > 0),
    options: (c) => {
      const ok = canAfford(c) || hasSponsor(c);
      return [opt('move.abroad', 'go', ok, ok ? undefined : 'reason.noMoney'), opt('move.abroad', 'stay')];
    },
    // Эмиграция — редкий путь: по умолчанию человек остаётся, уезжает один из десяти.
    timeout: (_c, options, rng) => firstAvailable(options, rng.chance(ABROAD_TIMEOUT_GO) ? 'go' : 'stay', 'stay'),
    apply: (c, id, rng) => {
      const flagged = withoutFlag(withFlags(c.life, { [FLAG_ABROAD_DECIDED]: true }), FLAG_ABROAD_CHANCE_DELTA);
      if (id !== 'go') {
        const life = shift(flagged, { relations: 1 });
        return { life, entries: [entry(life, 'move.abroad', 'stay')] };
      }
      const delta = num(c.life, FLAG_ABROAD_CHANCE_DELTA) ?? 0;
      const chance = Math.max(0, Math.min(1, ABROAD_BASE_CHANCE + (hasSponsor(c) ? ABROAD_SPONSOR_BONUS : 0) + (Number.isFinite(delta) ? delta : 0)));
      if (!rng.chance(chance)) {
        const life = shift(flagged, { happiness: ABROAD_FAIL_HAPPINESS });
        return { life, entries: [entry(life, 'move.abroad', 'go.fail')] };
      }
      let target = sponsorIso3(c);
      if (target === null) {
        const { iso3s, weights } = richerCountries(c);
        if (iso3s.length === 0) {
          const life = shift(flagged, { happiness: ABROAD_FAIL_HAPPINESS });
          return { life, entries: [entry(life, 'move.abroad', 'go.fail')] };
        }
        target = rng.weighted(iso3s, weights);
      }
      const paid = spend(flagged, ABROAD_COST_GDP * ind(c, 'gdpPpp'), false);
      const life = shift(paid, { relations: -10, happiness: 4, knowledge: 3 }, { iso3: target, urban: true });
      return {
        life,
        entries: [entry(life, 'move.abroad', 'go.success', { country: getCountry(c.ds, target).nameRu })],
      };
    },
  },
  {
    kind: 'love.partner',
    due: ({ life }) => !life.partner && inAge(life, LOVE_MIN_AGE, LOVE_MAX_AGE) && !askedRecently(life, FLAG_LOVE_ASKED_AGE, LOVE_ASK_EVERY),
    options: () => yesNo('love.partner', 'seek', 'alone'),
    timeout: (_c, options) => firstAvailable(options, 'seek'),
    apply: (c, id, rng) => {
      const flagged = withFlags(c.life, { [FLAG_LOVE_ASKED_AGE]: c.life.age });
      if (id !== 'seek') {
        const life = shift(flagged, { happiness: 1 });
        return { life, entries: [entry(life, 'love.partner', 'alone')] };
      }
      if (rng.chance(c.life.stats.relations / 100)) {
        const life = shift(flagged, { happiness: 8, relations: 5 }, { partner: true });
        return { life, entries: [entry(life, 'love.partner', 'seek.found')] };
      }
      const life = shift(flagged, { happiness: -2 });
      return { life, entries: [entry(life, 'love.partner', 'seek.alone')] };
    },
  },
  {
    kind: 'family.child',
    silent: ['wait'],
    due: ({ life }) => life.partner && inAge(life, CHILD_MIN_AGE, CHILD_MAX_AGE) && !askedRecently(life, FLAG_CHILD_ASKED_AGE, CHILD_ASK_EVERY),
    options: () => yesNo('family.child', 'have', 'wait'),
    // Столько детей, сколько в среднем в стране: иначе — подождать.
    timeout: (c, options) => firstAvailable(options, c.life.children < Math.round(ind(c, 'fertility')) ? 'have' : 'wait', 'wait'),
    apply: (c, id) => {
      const flagged = withFlags(c.life, { [FLAG_CHILD_ASKED_AGE]: c.life.age });
      const life =
        id === 'have'
          ? shift(spend(flagged, CHILD_COST_GDP * ind(c, 'gdpPpp'), false), { happiness: 5, relations: 3 }, { children: c.life.children + 1, childrenAges: [...c.life.childrenAges, 0] })
          : flagged;
      return { life, entries: [entry(life, 'family.child', id)] };
    },
  },
  {
    kind: 'health.treatment',
    due: treatmentDue,
    options: () => yesNo('health.treatment', 'treat', 'wait'),
    timeout: (c, options) =>
      firstAvailable(options, clampMoney(c.life.money) >= TREAT_COST_GDP * ind(c, 'gdpPpp') * (ind(c, 'oopHealth') / 100) ? 'treat' : 'wait', 'wait'),
    apply: (c, id) => {
      const patch: Record<string, number | boolean> = {};
      if (hasChronic(c.life)) patch[FLAG_CHRONIC_DECIDED] = true;
      const accident = num(c.life, FLAG_ACCIDENT_YEAR);
      if (accident !== undefined) patch[FLAG_ACCIDENT_DECIDED_YEAR] = accident;
      const flagged = withoutFlag(withFlags(c.life, patch), FLAG_TREATMENT_HEALTH, FLAG_TREATMENT_COST_SHARE);
      // Мини-игра «Очередь» (G8) может задать своё здоровье и долю цены; без неё — константы.
      const gain = num(c.life, FLAG_TREATMENT_HEALTH) ?? TREAT_HEALTH;
      const shareRaw = num(c.life, FLAG_TREATMENT_COST_SHARE) ?? 1;
      const share = Number.isFinite(shareRaw) ? Math.max(0, Math.min(1, shareRaw)) : 1;
      const life =
        id === 'treat'
          ? shift(spend(flagged, share * TREAT_COST_GDP * ind(c, 'gdpPpp') * (ind(c, 'oopHealth') / 100), true), { health: gain })
          : shift(flagged, { health: WAIT_HEALTH });
      return { life, entries: [entry(life, 'health.treatment', id)] };
    },
  },
  {
    kind: 'money.invest',
    silent: ['save'],
    due: ({ life }) =>
      life.job !== null &&
      life.job.income > 0 &&
      clampMoney(life.money) > INVEST_MONEY_TO_INCOME * life.job.income &&
      !askedRecently(life, FLAG_INVEST_ASKED_AGE, INVEST_ASK_EVERY),
    options: () => yesNo('money.invest', 'invest', 'save'),
    timeout: (_c, options) => firstAvailable(options, 'save'),
    apply: (c, id, rng) => {
      const flagged = withFlags(c.life, { [FLAG_INVEST_ASKED_AGE]: c.life.age });
      if (id !== 'invest') return { life: flagged, entries: [entry(flagged, 'money.invest', 'save')] };
      const win = rng.chance(INVEST_WIN_CHANCE);
      const money = clampMoney(c.life.money) * (win ? INVEST_WIN_MULT : INVEST_LOSS_MULT);
      const life = shift({ ...flagged, money: clampMoney(money) }, { happiness: win ? 4 : -5 });
      return { life, entries: [entry(life, 'money.invest', win ? 'invest.gain' : 'invest.loss')] };
    },
  },
  {
    kind: 'work.retire',
    silent: ['continue'],
    due: ({ life }) =>
      life.job !== null && !retired(life) && inAge(life, RETIRE_MIN_AGE, RETIRE_MAX_AGE) && !askedRecently(life, FLAG_RETIRE_ASKED_AGE, RETIRE_ASK_EVERY),
    options: () => yesNo('work.retire', 'retire', 'continue'),
    timeout: (c, options) => firstAvailable(options, c.life.age >= RETIRE_TIMEOUT_AGE ? 'retire' : 'continue', 'continue'),
    apply: (c, id) => {
      const flagged = withFlags(c.life, { [FLAG_RETIRE_ASKED_AGE]: c.life.age });
      if (id !== 'retire') return { life: flagged, entries: [entry(flagged, 'work.retire', 'continue')] };
      const share = ind(c, 'gdpPpp') > PENSION_RICH_GDP ? PENSION_RICH_SHARE : PENSION_POOR_SHARE;
      const pension = clampMoney(c.life.job!.income * share);
      const life = shift(withFlags(flagged, { [FLAG_RETIRED]: true, [FLAG_PENSION]: pension }), { happiness: 2, health: 1 }, { job: null });
      return { life, entries: [entry(life, 'work.retire', 'retire')] };
    },
  },
  {
    kind: 'family.careParents',
    due: ({ life }) => inAge(life, CARE_MIN_AGE, CARE_MAX_AGE) && parentsAlive(life) > 0 && !life.flags[FLAG_CARE_DECIDED],
    options: () => yesNo('family.careParents', 'care', 'no'),
    timeout: (_c, options) => firstAvailable(options, 'care'),
    apply: (c, id) => {
      const flagged = withFlags(c.life, { [FLAG_CARE_DECIDED]: true });
      const life =
        id === 'care'
          ? shift(spend(flagged, CARE_COST_GDP * ind(c, 'gdpPpp'), false), { relations: 5 })
          : shift(flagged, { relations: -5 });
      return { life, entries: [entry(life, 'family.careParents', id)] };
    },
  },
];
